import {
  type DefaultError,
  type MutationObserverOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/vue-query';
import keyBy from 'lodash-es/keyBy';
import { computed, ComputedRef, MaybeRefOrGetter, toValue } from 'vue';

import { TimestampedResponse } from '@/models';
import { PaginatedResponse } from '@/models/api';
import { Contact } from '@/models/contact';
import { Customer, CustomerCompanyUser, ServiceZone, Zone, ZoneZipcode } from '@/models/customer';
import { CustomerContact } from '@/models/customerContact';
import { Product } from '@/models/product';
import { i18n } from '@/plugins';
import api from '@/services/api';
import {
  addContact,
  addCustomer,
  addZone,
  bulkSaveZones,
  type BulkSaveZonesRequestPayload,
  deleteContact,
  getContacts,
  getCustomerCompanyUsersList,
  getCustomerContacts,
  getCustomerList,
  getCustomerRecordById,
  getDistributionZone,
  getDistributionZones,
  getEligibleBuyersForProducts,
  getPotentialCustomerList,
  getServiceZones,
  getZips,
  removeZone,
  updateContact,
  updateCustomer,
  updateCustomerContact,
  updateZone,
  type UpdateZoneRequestPayload,
} from '@/services/api/CRM';
import { PotentialCustomer } from '@/services/api/models/customer';
import parsers from '@/services/parsers';
import { CustomerParsed } from '@/services/parsers/CRM';
import { handleError } from '@/utils/errors';

/**
 * query key can take an object of additional options
 * @see https://tanstack.com/query/v4/docs/framework/vue/guides/query-keys#array-keys-with-variables
 */
export const crmQueryKeys = {
  customers: (payload?: BaseGetCustomerArgs) => ['crm', 'customers', payload] as const,
  customerById: (payload: BaseGetCustomerArgs & { customerId: MaybeRefOrGetter<number | string | undefined> }) => {
    const { customerId, ...restPayload } = payload;

    return [...crmQueryKeys.customers(restPayload), customerId];
  },
  customerCompanyUsers: () => ['crm', 'customerCompanyUsers'] as const,
  customersWithOrders: (payload: {
    archived?: MaybeRefOrGetter<boolean | undefined>;
    search?: MaybeRefOrGetter<string | undefined>;
    timestamp?: MaybeRefOrGetter<string | undefined>;
  }) => ['crm', 'customersWithOrders', payload] as const,
  potentialCustomers: (payload: { queryString: MaybeRefOrGetter<string> }) => ['crm', 'potentialCustomers', payload],
  contacts: () => ['crm', 'contacts'] as const,
  customerContacts: (payload: BaseGetCustomerContactsArgs) => ['crm', 'customerContacts', payload] as const,
  distributionZones: (payload?: BaseGetDistributionZoneArgs) => ['crm', 'distributionZones', payload] as const,
  distributionZoneById: (payload: { zoneId: MaybeRefOrGetter<string> }) => {
    const { zoneId } = payload;

    return [...crmQueryKeys.distributionZones(), zoneId];
  },
  zoneZipCodes: () => ['crm', 'zoneZipCodes'] as const,
  serviceZones: () => ['crm', 'serviceZones'] as const,
  eligibleBuyersForProducts: (payload: UseEligibleBuyersForProductsQuerArgs) =>
    ['crm', 'eligibleBuyersForProducts', payload] as const,
};

type BaseGetCustomerArgs = {
  archived?: MaybeRefOrGetter<boolean | undefined>;
  isBrandOwner?: MaybeRefOrGetter<boolean | undefined>;
  order_split_eligibility?: MaybeRefOrGetter<string | undefined>;
  owner?: MaybeRefOrGetter<number | undefined>;
  search?: MaybeRefOrGetter<string | undefined>;
};

type BaseGetCustomerContactsArgs = {
  contactArchived?: MaybeRefOrGetter<boolean>;
  customerArchived?: MaybeRefOrGetter<boolean>;
};

type BaseGetDistributionZoneArgs = {
  fields?: MaybeRefOrGetter<Array<keyof Zone>>;
  zipcodes?: MaybeRefOrGetter<number[]>;
};

type UseCustomerListQueryArgs = Partial<UseQueryOptions<CustomerParsed[]>> & BaseGetCustomerArgs;

export const useCustomerListQuery = ({
  archived,
  isBrandOwner,
  order_split_eligibility,
  owner,
  search,
  ...options
}: UseCustomerListQueryArgs = {}) => {
  return useQuery({
    queryKey: crmQueryKeys.customers({ archived, isBrandOwner, order_split_eligibility, owner, search }),
    queryFn: () =>
      getCustomerList({
        archived: toValue(archived),
        isBrandOwner: toValue(isBrandOwner),
        order_split_eligibility: toValue(order_split_eligibility),
        owner: toValue(owner),
        search: toValue(search),
      }),
    ...options,
  });
};

export const getCustomerFromList = ({ customerListQuery, customerId }) => {
  return computed(() => {
    return customerListQuery.data.value?.find(
      (customer) => customer.id && customerId.value && String(customer.id) === String(customerId.value),
    );
  });
};

export const getCustomerMap = computed(() => {
  const { data } = useCustomerListQuery();
  const dataValue = toValue(data);

  return ({ key } = { key: 'id' }) => (dataValue ? keyBy(dataValue, key) : undefined);
});

export function getCustomersKeyedById({ customerListQuery }): ComputedRef {
  return computed(() => keyBy(customerListQuery.data?.value, 'id'));
}

export function getCustomersKeyedByPartnerId({ customerListQuery }): ComputedRef {
  return computed(() => keyBy(customerListQuery.data?.value, 'partnerId'));
}

type UseCustomerByIdQueryArgs = Partial<UseQueryOptions<Customer | undefined>> &
  BaseGetCustomerArgs & { customerId: MaybeRefOrGetter<number | string | undefined> };

export const useCustomerByIdQuery = ({
  archived,
  customerId,
  isBrandOwner,
  order_split_eligibility,
  owner,
  search,
  ...options
}: UseCustomerByIdQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.customerById({
      archived,
      customerId,
      isBrandOwner,
      order_split_eligibility,
      owner,
      search,
    }),
    queryFn: () => {
      if (!toValue(customerId)) {
        return undefined;
      }

      return getCustomerRecordById({
        archived: toValue(archived),
        customerId: toValue(customerId),
        isBrandOwner: toValue(isBrandOwner),
        order_split_eligibility: toValue(order_split_eligibility),
        owner: toValue(owner),
        search: toValue(search),
      });
    },
    enabled: computed(() => !!toValue(customerId)),
    ...options,
  });
};

type UseCustomersWithOrdersQueryArgs = UseQueryOptions<TimestampedResponse> & {
  archived?: MaybeRefOrGetter<boolean | undefined>;
  search?: MaybeRefOrGetter<string | undefined>;
  timestamp?: MaybeRefOrGetter<string | undefined>;
};

export const useCustomersWithOrdersQuery = ({
  archived,
  search,
  timestamp,
  ...options
}: UseCustomersWithOrdersQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.customersWithOrders({ archived, search, timestamp }),
    queryFn: async () => {
      try {
        const customerList = await api.CRM.getCustomerList({
          archived: toValue(archived),
          search: toValue(search),
        });

        const customerStats = await api.CRM.getStats();

        // join customers with stats
        const customersWithStats = customerList.map((customer) => {
          const stats = customerStats[customer.id];

          return {
            ...customer,
            ...stats,
          };
        });

        return {
          results: customersWithStats,
          timestamp: Number(timestamp),
        };
      } catch (error) {
        handleError(error, i18n.t('crm.notify.fetchCustomersError'));
      }
    },
    ...options,
  });
};

type UsePotentialCustomersListQueryArgs = UseQueryOptions<PaginatedResponse<PotentialCustomer>> & {
  queryString: MaybeRefOrGetter<string>;
};

export const usePotentialCustomersListQuery = ({ queryString, ...options }: UsePotentialCustomersListQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.potentialCustomers({ queryString }),
    queryFn: () => getPotentialCustomerList({ queryString: toValue(queryString) }),
    ...options,
  });
};

type UseCreateCustomerMutationArgs = Partial<MutationObserverOptions<Customer, DefaultError, { customer: Customer }>>;

export function useCreateCustomerMutation(options?: UseCreateCustomerMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customer }) => addCustomer(customer),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.customers(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseUpdateCustomerMutationArgs = Partial<MutationObserverOptions<Customer, DefaultError, { customer: Customer }>>;

export function useUpdateCustomerMutation(options?: UseUpdateCustomerMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customer }) => updateCustomer(customer),
    ...options,
    onSuccess: (...args) => {
      const { id } = args[0];

      queryClient.refetchQueries({ queryKey: crmQueryKeys.customers(), exact: true });
      queryClient.refetchQueries({ queryKey: crmQueryKeys.customerById({ customerId: id }) });

      options?.onSuccess?.(...args);
    },
  });
}

type UseContactListQueryArgs = Partial<UseQueryOptions<Contact[]>>;

export const useContactsListQuery = (options?: UseContactListQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.contacts(),
    queryFn: () => getContacts(),
    ...options,
  });
};

export const getContactFromList = computed(() => {
  const { data } = useContactsListQuery();

  return ({ id }: { id: Customer['id'] }) =>
    toValue(data)?.find((contact) => contact.id && id && String(contact.id) === String(id));
});

type UseCreateContactMutationArgs = Partial<MutationObserverOptions<Contact, DefaultError, { contact: Contact }>>;

export function useCreateContactMutation(options?: UseCreateContactMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contact }) => addContact(contact),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.contacts(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseUpdateContactMutationArgs = Partial<MutationObserverOptions<Contact, DefaultError, { contact: Contact }>>;

export function useUpdateContactMutation(options?: UseUpdateContactMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contact }) => updateContact(contact),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.contacts(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseDeleteContactMutationArgs = Partial<MutationObserverOptions<Contact, DefaultError, { contact: Contact }>>;

export function useDeleteContactMutation(options?: UseDeleteContactMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contact }) => deleteContact(contact),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.contacts(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseCustomersContactsQueryArgs = UseQueryOptions & BaseGetCustomerContactsArgs;

export const useCustomerContactsListQuery = ({
  contactArchived = false,
  customerArchived = false,
  ...options
}: UseCustomersContactsQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.customerContacts({ contactArchived, customerArchived }),
    queryFn: () =>
      getCustomerContacts({ customerArchived: toValue(customerArchived), contactArchived: toValue(contactArchived) }),
    ...options,
  });
};

type UseUpdateCustomerContactMutationArgs = Partial<
  MutationObserverOptions<CustomerContact, DefaultError, { customerContact: CustomerContact }>
>;

export function useUpdateCustomerContactMutation(options?: UseUpdateCustomerContactMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerContact }) => updateCustomerContact(customerContact),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.contacts(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseDistributionZonesQueryArgs = Partial<UseQueryOptions<Zone[]> & BaseGetDistributionZoneArgs>;

export const useDistributionZonesQuery = ({ fields, zipcodes, ...options }: UseDistributionZonesQueryArgs = {}) => {
  return useQuery({
    queryKey: crmQueryKeys.distributionZones({ fields, zipcodes }),
    queryFn: () => getDistributionZones({ fields: toValue(fields), zipcodes: toValue(zipcodes) }),
    ...options,
  });
};

export const getActiveDistributionZonesFromList = computed(() => {
  const { data } = useDistributionZonesQuery();

  return toValue(data)?.filter((zone) => zone.isActive);
});

type UseDistributionZoneByIdQueryArgs = UseQueryOptions & { zoneId: MaybeRefOrGetter<string> };

export const useDistributionZoneByIdQuery = ({ zoneId, ...options }: UseDistributionZoneByIdQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.distributionZoneById({ zoneId }),
    queryFn: () => getDistributionZone(toValue(zoneId)),
    ...options,
  });
};

type UseCreateZoneMutationArgs = Partial<MutationObserverOptions<Zone, DefaultError, { zone: Omit<Zone, 'id'> }>>;

export function useCreateZoneMutation(options?: UseCreateZoneMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zone }) => addZone(zone),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.distributionZones(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseUpdateZonetMutationArgs = Partial<
  MutationObserverOptions<Zone, DefaultError, { zone: UpdateZoneRequestPayload }>
>;

export function useUpdateZoneMutation(options?: UseUpdateZonetMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zone }) => updateZone(zone),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.distributionZones(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseBulkCreateZonesMutationArgs = Partial<
  MutationObserverOptions<Zone, DefaultError, { zones: BulkSaveZonesRequestPayload[] }>
>;

export function useBulkCreateZonesMutation(options?: UseBulkCreateZonesMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zones }) => bulkSaveZones(zones),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: crmQueryKeys.distributionZones(), exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseDeleteZoneMutationArgs = Partial<MutationObserverOptions<Zone, DefaultError, { zone: Zone }>>;

export function useDeleteZoneMutation(options?: UseDeleteZoneMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zone }) => removeZone(zone),
    ...options,
    onSuccess: (...args) => {
      const { id } = args[0];

      queryClient.refetchQueries({ queryKey: crmQueryKeys.distributionZones(), exact: true });
      queryClient.removeQueries({ queryKey: crmQueryKeys.distributionZoneById({ zoneId: String(id) }) });

      options?.onSuccess?.(...args);
    },
  });
}

type UseZoneZipCodesQueryArgs = Partial<UseQueryOptions<ZoneZipcode[]>>;

export const useZoneZipCodesQuery = (options?: UseZoneZipCodesQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.zoneZipCodes(),
    queryFn: () => getZips(),
    ...options,
  });
};

type UseServiceZonesQueryArgs = Partial<UseQueryOptions<ServiceZone[]>>;

export const useServiceZonesQuery = (options?: UseServiceZonesQueryArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.serviceZones(),
    queryFn: () => getServiceZones(),
    ...options,
  });
};

export const getServicesZones = computed(() => {
  const { data } = useServiceZonesQuery();
  const dataValue = toValue(data);

  return ({ key } = { key: 'id' }) => (dataValue ? keyBy(dataValue, key) : undefined);
});

type UseEligibleBuyersForProductsQuerArgs = Product['id'][];

export const useEligibleBuyersForProductsQuery = (productIds: UseEligibleBuyersForProductsQuerArgs) => {
  return useQuery({
    queryKey: crmQueryKeys.eligibleBuyersForProducts(productIds),
    queryFn: () => getEligibleBuyersForProducts(toValue(productIds)),
  });
};

interface CustomerKeyedByIdMap {
  [id: number]: Customer;
}

type GetParsedEligibleBuyersArgs = {
  eligibleBuyersForProduct: ReturnType<typeof useEligibleBuyersForProductsQuery>;
  customersKeyedById: ComputedRef<CustomerKeyedByIdMap>;
};

export function getParsedEligibleBuyers({ eligibleBuyersForProduct, customersKeyedById }: GetParsedEligibleBuyersArgs) {
  return computed(() => {
    if (eligibleBuyersForProduct.data?.value && customersKeyedById.value) {
      return parsers.CRM.buildEligibleBuyers(eligibleBuyersForProduct.data?.value, customersKeyedById.value);
    }

    return [];
  });
}

export const useCustomerCompanyUsersListQuery = (options?: UseQueryOptions<CustomerCompanyUser[]>) => {
  return useQuery<CustomerCompanyUser[]>({
    queryKey: crmQueryKeys.customerCompanyUsers(),
    queryFn: () => getCustomerCompanyUsersList(),
    ...options,
  });
};
